import { createGlobalStyle } from 'styled-components'
import { normalize } from 'styled-normalize'

import { LoadingIndicator } from './LoadingIndicator.jsx'
import { getTheme } from './theme.js'
import tageditorStyle from './ui/tageditorStyle.js'
import { StateObjMultiple } from './utils/stateObj.js'

const GlobalStyle = createGlobalStyle`
  .atrius-map
  {

    ${normalize}
    ${tageditorStyle}

    box-sizing: border-box;
    font-size: 100%;
    touch-action: manipulation;

    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primaryText};
    font-family: ${props => props.theme.fonts.family};
    font-weight: normal;
    line-height: 1.5;
    font-size: 16px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overscroll-behavior-y: none;
    text-align: left;

  /*
      The following rule mostly fixes the secondary rubber banding issue
      (see https://stackoverflow.com/questions/37586286/prevent-overflow-rubberband-scrolling-on-ios)
      but it does so via fixed positioning - which would effect usage in SDK, etc - so needs
      to be carefully considered. So we are commening it out for now.

    @supports (-webkit-touch-callout: none) {
      position: fixed;
      width: 100vw;
      height: -webkit-fill-available;
    }
  */

    .maplibregl-canvas-container canvas { left: 0; top: 0 }

    *,
    *::before,
    *::after {
      box-sizing: inherit;
    }

    h1, h2, h3, h4, h5, h6, p {
      font-family: ${props => props.theme.fonts.family};
      font-style: normal;
      font-weight: normal;
    }

    h1 { ${({ theme }) => theme.fontSize('h1')}; }
    h2 { ${({ theme }) => theme.fontSize('h2')}; }
    h3 { ${({ theme }) => theme.fontSize('h3')}; }
    h4 { ${({ theme }) => theme.fontSize('h4')}; }
    h5 { ${({ theme }) => theme.fontSize('h5')}; }
    h6 { ${({ theme }) => theme.fontSize('h6')}; }
    p { ${({ theme }) => theme.fontSize('h3')}; }

    @media print, screen and (min-width: 40em) {
      h1 { ${({ theme }) => theme.fontSize('26px')}; }
      h2 { ${({ theme }) => theme.fontSize('20px')}; }
      h3 { ${({ theme }) => theme.fontSize('18px')}; }
      h4 { ${({ theme }) => theme.fontSize('16px')}; }
      h5 { ${({ theme }) => theme.fontSize('26px')}; text-transform: uppercase; letter-spacing: 0.5rem; }
      h6 { ${({ theme }) => theme.fontSize('12px')}; }
      p { ${({ theme }) => theme.fontSize('18px')}; }
    }

    .nopointer { pointer-events: none; }
  }
`

const initThemeManager = (app) => {
  const themeState = new StateObjMultiple({ theme: {}, contentURL: null })

  app.bus.on('venueData/venueDataLoaded', () =>
    app.bus.get('venueData/getContentUrl', { type: 'web-engine-ui' })
      .then(url => themeState.update({ contentURL: url })))

  app.bus.on('theme/getVenueLogo', () =>
    app.bus.get('venueData/getContentUrl', { type: 'spritesheet-svg', name: `venue-logo.svg` }))

  app.bus.on('debugTools/fileDrop', async ({ file, content }) => {
    const theme = themeState.getState().theme
    if (file.type === 'application/json') {
      const jsonOb = JSON.parse(content)
      if (jsonOb.theme)
        for (const cn in jsonOb.theme.colors)
          theme.colors[cn] = jsonOb.theme.colors[cn]
    }
  })

  const updateTheme = async (themeDef, defaultTheme) => {
    const theme = await getTheme(themeDef, defaultTheme)
    theme.getBaseUIUrl = () => themeState.getState().contentURL
    themeState.update({ theme })
  }

  const buildTheme = async (themeDef, defaultTheme) => {
    await updateTheme(themeDef, defaultTheme)
    return { getTheme: () => themeState.getState().theme, GlobalStyle, Loading: LoadingIndicator, updateTheme, getThemeState: () => themeState }
  }

  return { buildTheme }
}

export { initThemeManager }
